import {
    assign,
    getApi,
    getOdmId,
    getTimezone,
    IExtObj,
    isEmpty,
    isNumber,
    JSONParse,
    localStorageManager,
    post,
    setTrackerConfig,
    tracker
} from '~/utils';
import { API, ECode, LOCAL_STORAGE, LOCAL_STORAGE_MIGRATE, SuiteId, TRACKER } from '~/constants';
import { TenantConf } from '~/interfaces';
import { MemberType, rsaVerifySignature } from '@fx-ui/platform-sdk';
import Intl from '@fx-ui/intl';

/**
 * 创建新企业
 */
export const createCorp = ({
    corpName,
    ...otherParams
}: {
    corpName?: string;
    suite_id?: number;
    suite_metadata?: string;
}) => {
    const data = {
        timezone: getTimezone(),
        region: navigator.language,
        locale: Intl.getLocaleKey(
            JSONParse(
                Intl.determineLocale({
                    localStorageLocaleKey: LOCAL_STORAGE_MIGRATE.LANG
                }).toLowerCase()
            )
        ),
        odm_id: getOdmId(),
        ...otherParams
    };
    if (!isEmpty(corpName)) {
        assign(data, { name: corpName });
    }
    return post(getApi(API.corp.create_tenant), data);
};

/**
 * 创建企业
 * 适用于注册场景下调用
 * @param config 配置参数
 * @param config.corpName 创建的企业名称
 * @param config.trackParams 埋点参数
 */
export const createCorpInRegister = (config?: {
    corpName?: string;
    trackParams?: IExtObj;
    suiteId?: SuiteId;
}) => {
    const { corpName, trackParams, suiteId } = config || {};

    const params = {
        corpName
    };
    const currentSuiteId = suiteId ?? window.platform_suite_id;
    if (isNumber(currentSuiteId)) {
        assign(params, {
            suite_id: currentSuiteId
        });
    }
    if (window.platform_suite_metadata) {
        assign(params, {
            suite_metadata: window.platform_suite_metadata
        });
    }
    return createCorp(params).then(({ data }) => {
        const { user, member, tenant, vip } = data;

        setTrackerConfig({
            user_id: user?._id ?? '',
            username:
                user?.username ??
                localStorageManager.getItemWithMigrate({
                    oldKey: LOCAL_STORAGE.USER_COOKIE_ID,
                    newKey: LOCAL_STORAGE_MIGRATE.USER_COOKIE_ID
                }),
            corp_id: tenant?.corp_id ?? '',
            tenant_id: tenant?.id ?? '',
            member_id: member?.member_id ?? '',
            is_owner: member?.is_owner ?? false,
            is_admin: member?.is_admin ?? false,
            is_platform_manager: member?.is_platform_manager ?? false,
            is_external_user: member?.type === MemberType.external,
            vip_level: vip?.pack.level ?? ''
        });
        tracker(TRACKER.USAGE_MODE, {
            method: 'create',
            corpName: tenant?.name ?? '',
            ...trackParams
        });
        return data;
    });
};

/**
 * 切换企业
 * @param tenantId 租户 ID
 */
export const switchCorp = (tenantId: string) => {
    return post(
        getApi(API.authentication.switch_corp),
        { target: tenantId },
        {
            customHandleErrorCodes: [ECode.USER_MFA_REQUIRED, ECode.USER_PHONE_UNBIND_BEFORE_MFA]
        }
    );
};

/**
 * 获取企业配置
 */
export const fetchCorpProfile = (tenantId: string) => {
    return post<TenantConf>(getApi(API.conf.get_tenant_conf, tenantId)).then((res) => {
        const profile = res.data;
        const ssoConfig = profile.sso_config?.config;
        if (ssoConfig && 'secret' in ssoConfig) {
            // 解密secret
            ssoConfig.secret = rsaVerifySignature(ssoConfig.secret) || ssoConfig.secret;
        }
        return profile;
    });
};
